import { Add, Inventory2Outlined } from "@mui/icons-material";
import { Alert, Box, Button, Card, Chip, CircularProgress, Stack, Typography } from "@mui/joy";
import { useMemo, useState } from "react";

import {
  useBulkUpdateRouteLabelGroupsMutation,
  useBulkUpdateRouteLabelsMutation,
  useRouteLabelConfigQuery,
} from "@/hooks/routeLabels";
import { useCurrentVenueQuery } from "@/hooks/venues";

import { ButtonLink } from "../Links";
import { useModal } from "../modals/useModal";
import LabelGroupRow from "./LabelGroupRow";
import {
  buildLabelList,
  getEntryUuid,
  reorderIds,
  splitRouteLabelConfig,
  toListOrderUpdates,
} from "./routeLabelsConfig";
import { RouteLabelsToast } from "./routeLabelsToast";
import SingleLabelRow from "./SingleLabelRow";
import { ROWS_SCOPE, useSortableMonitor } from "./sortable";

export default function RouteLabelsSettings() {
  const { data: venue } = useCurrentVenueQuery();
  const { data: config, isPending, isError } = useRouteLabelConfigQuery();
  const bulkUpdateGroups = useBulkUpdateRouteLabelGroupsMutation();
  const bulkUpdateLabels = useBulkUpdateRouteLabelsMutation();
  const { display: displayModal } = useModal();

  const { activeGroups, activeRootLabels, archivedCount } = useMemo(
    () => splitRouteLabelConfig(config),
    [config],
  );
  const entries = useMemo(
    () => buildLabelList(activeGroups, activeRootLabels),
    [activeGroups, activeRootLabels],
  );

  // Labels with options start collapsed, apart from one that was just added.
  const [expandedUuids, setExpandedUuids] = useState<Set<string>>(new Set());
  const setExpanded = (uuid: string, expanded: boolean) =>
    setExpandedUuids((current) => {
      const next = new Set(current);
      if (expanded) {
        next.add(uuid);
      } else {
        next.delete(uuid);
      }
      return next;
    });

  useSortableMonitor(ROWS_SCOPE, "vertical", (sourceId, targetId, edge) => {
    const orderedUuids = reorderIds(entries.map(getEntryUuid), sourceId, targetId, edge);
    // The groups and the root labels are saved by separate endpoints.
    const { groupUpdates, labelUpdates } = toListOrderUpdates(orderedUuids, entries);
    if (groupUpdates.length > 0) {
      bulkUpdateGroups.mutate(groupUpdates);
    }
    if (labelUpdates.length > 0) {
      bulkUpdateLabels.mutate(labelUpdates);
    }
  });

  if (venue && venue.userRole !== "ADMIN") {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Only gym admins can manage route labels.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5, height: "100dvh", overflow: "auto" }}>
      <Box sx={{ maxWidth: 860 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography level="title-lg">Route Labels</Typography>
          <Button
            startDecorator={<Add />}
            onClick={() =>
              displayModal("ADD_ROUTE_LABEL", {
                onCreated: (uuid, hasOptions) => {
                  if (hasOptions) {
                    setExpanded(uuid, true);
                  }
                },
              })
            }
          >
            Add label
          </Button>
        </Stack>
        <Typography level="body-sm" sx={{ mt: 1, mb: 2.5, maxWidth: 640 }}>
          Add custom labels to help you organise and analyse your routes.
        </Typography>

        {isPending ? <CircularProgress /> : null}
        {isError ? <Alert color="danger">Couldn’t load the route labels.</Alert> : null}

        {config ? (
          <>
            <Card variant="outlined" sx={{ p: 0, gap: 0, overflow: "hidden" }}>
              {entries.length === 0 ? (
                <Typography level="body-sm" sx={{ p: 2 }}>
                  No labels yet!
                </Typography>
              ) : null}
              {entries.map((entry) =>
                entry.kind === "group" ? (
                  <LabelGroupRow
                    key={entry.group.uuid}
                    group={entry.group}
                    expanded={expandedUuids.has(entry.group.uuid)}
                    onToggle={() =>
                      setExpanded(entry.group.uuid, !expandedUuids.has(entry.group.uuid))
                    }
                    onExpand={() => setExpanded(entry.group.uuid, true)}
                  />
                ) : (
                  <SingleLabelRow key={entry.label.uuid} label={entry.label} />
                ),
              )}
            </Card>

            {archivedCount > 0 ? (
              <Box sx={{ mt: 3.5, pt: 2.5, borderTop: "1px solid", borderColor: "divider" }}>
                <ButtonLink
                  from="/gym/$venueUuid/settings/labels"
                  to="./archive"
                  variant="outlined"
                  color="neutral"
                  startDecorator={<Inventory2Outlined />}
                  endDecorator={<Chip size="sm">{archivedCount}</Chip>}
                >
                  View archive
                </ButtonLink>
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
      <RouteLabelsToast />
    </Box>
  );
}
